/**
 * Injectable telemetry sink for the rule engine.
 *
 * Replaces direct imports of `~/logging/applicationInsightsService`, which is a
 * module-load singleton: it constructs a ReactPlugin and reads
 * `import.meta.env` at import time. That makes it unusable outside the web
 * app's Vite build — `import.meta` is a hard build error on native targets —
 * and it means merely importing the expression sandbox in a test boots a real
 * Application Insights client.
 *
 * Two named methods rather than one generic `track(payload, method)`. The
 * engine says WHAT happened; mapping that onto Application Insights'
 * TRACK_METHOD is the app's business, so `TRACK_METHOD` never has to cross into
 * the package. Both current call sites are covered:
 *
 *   trackException — a designer expression threw (expression-sandbox)
 *   trackTrace     — a Computation produced the wrong type (type-mismatch-reporting)
 *
 * The app injects an implementation at its composition root. Defaults are
 * no-ops so the engine stays usable in tests and a plain Node harness.
 *
 * ⚠️ An unwired seam is silent — failures simply vanish rather than erroring.
 * `formEngineWiring.test.js` exists to make that impossible to ship. (The
 * Viedoc.Me.ReactNative POC built this exact seam and never called the setter.)
 */

/**
 * Application Insights SeverityLevel values.
 *
 * Named rather than inlined as bare 2/3 literals: the numbers are meaningless
 * at the call site, and the engine should not have to know App Insights'
 * encoding to say how bad something is.
 */
export const SEVERITY = Object.freeze({
    VERBOSE: 0,
    INFORMATION: 1,
    WARNING: 2,
    ERROR: 3,
});

/**
 * @typedef {object} TelemetryAdapter
 * @property {(payload: { exception: unknown, properties?: object, severityLevel?: number }) => void} trackException
 * @property {(payload: { message: string, properties?: object, severityLevel?: number }) => void} trackTrace
 */

/** @type {TelemetryAdapter} */
const noopTelemetry = Object.freeze({
    trackException() {
        // Deliberately silent. The app injects the real sink.
    },
    trackTrace() {},
});

let currentTelemetry = noopTelemetry;

/** Inject the real telemetry sink. Call once, at the app's composition root. */
export function setTelemetry(adapter) {
    currentTelemetry = adapter;
}

export function getTelemetry() {
    return currentTelemetry;
}

/**
 * Restore the no-op default.
 *
 * This is module-level mutable state, so a test that injects a sink leaks into
 * every other test file sharing the worker unless it resets. Call from
 * `afterEach` wherever `setTelemetry` is used.
 */
export function resetTelemetry() {
    currentTelemetry = noopTelemetry;
}
